import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, MenuItem, Select, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  deleteUser,
  updatePassword,
  updateUserAttributes,
} from 'aws-amplify/auth';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { REMINDER_SCHEDULES } from '../../services/meal_plan_reminders';
import {
  countryCodeFromGeoRegion,
  GEO_REGIONS,
} from '../../services/geo_regions';
import { signOutAction } from '../../action_creators/user_action_creators';
import DeleteAccountPopup from './delete_account_popup';
import { sendAccountDeletedNotificationOperation } from '../../operations/user_profile_operations';
import { LOGIN_ROUTE } from '../../services/routes';
import { isEmbeddedInSmorgCompanion } from '../../services/smorg_companion_auth';
import { SpaceMembershipState } from '../../API';
import { linkWithQueryParams } from '../../services/urls';
import { MINIMUM_PASSWORD_LENGTH } from '../../services/users';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import {
  useGetMembershipTiersBySpaceQuery,
  useUpdateUserProfileMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { DEFAULT_SPACE_ID } from '../../services/spaces';
import { updateContactAttributeOperation } from '../../operations/email_list_operations';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetEndUserSpaceQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import UserProfileManageSubscriptionButton from './user_profile_manage_subscription_button';
import AppTechnicalInfoLabel from './app_technical_info_label';
import {
  currentUserSelector,
  dispatchAmplifyUser,
} from '../../features/user_id/user_id_slice';
import DeleteAccountDisallowedBeforeExpirationPopup from './delete_account_disallowed_before_expiration_popup';
import { isTestUserExpiryDate } from '../../services/smorg_studio_memberships';

const UserProfileEndUserDesktopView = () => {
  const navigate = useNavigate();

  const { data: userProfile } = useGetUserProfileQuery();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const { data: currentSpace } = useGetEndUserSpaceQuery();

  const {
    username: userId,
    email,
    givenName: initialGivenName,
    familyName: initialFamilyName,
  } = useSelector(currentUserSelector);
  const [givenName, setGivenName] = useState(initialGivenName);
  const [familyName, setFamilyName] = useState(initialFamilyName);
  const [oldPassword, setOldPassword] = useState('');
  const [cleartextPassword, setCleartextPassword] = useState('');
  const [geoRegion, setGeoRegion] = useState(userProfile?.geoRegion);
  const [mealPlanReminderSchedule, setMealPlanReminderSchedule] = useState(
    userProfile?.mealPlanReminderSchedule,
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [deleteAccountPopupVisible, setDeleteAccountPopupVisible] =
    useState(false);

  const [
    deleteAccountDisallowedBeforeExpirationPopupVisible,
    setDeleteAccountDisallowedBeforeExpirationPopupVisible,
  ] = useState(false);

  const dispatch = useDispatch();

  const skipNonEssentialFields = isEmbeddedInSmorgCompanion();

  useEffect(() => {
    setGivenName(initialGivenName);
    setFamilyName(initialFamilyName);
  }, [initialFamilyName, initialGivenName]);

  useEffect(() => {
    if (userProfile) {
      console.log('User profile changed');
      setGeoRegion(userProfile.geoRegion);
      setMealPlanReminderSchedule(userProfile.mealPlanReminderSchedule);
    }
  }, [userProfile]);

  const [updateUserProfile] = useUpdateUserProfileMutation();

  const saveUpdates = async () => {
    const promises = [];
    if (cleartextPassword && oldPassword) {
      promises.push(
        updatePassword(oldPassword, cleartextPassword).then(
          (changePasswordResult) => {
            console.log({ changePasswordResult });
          },
        ),
      );
    }
    const attributesToUpdate = {};
    if (givenName) {
      attributesToUpdate.given_name = givenName;
      promises.push(
        updateContactAttributeOperation(email, 'givenName', givenName),
      );
    }
    if (familyName) {
      attributesToUpdate.family_name = familyName;
      promises.push(
        updateContactAttributeOperation(email, 'familyName', familyName),
      );
    }
    if (Object.keys(attributesToUpdate).length > 0) {
      promises.push(
        updateUserAttributes({ userAttributes: attributesToUpdate }).then(
          (updateUserAttributesResult) => {
            console.log({ updateUserAttributesResult });
          },
        ),
      );
    }

    let countryCode;
    if (geoRegion) {
      countryCode = countryCodeFromGeoRegion(geoRegion);
    }

    const newUserProfile = { ...userProfile };

    if (geoRegion) {
      newUserProfile.geoRegion = geoRegion;
    }

    if (countryCode) {
      newUserProfile.countryCode = countryCode;
    }

    if (mealPlanReminderSchedule) {
      newUserProfile.mealPlanReminderSchedule = mealPlanReminderSchedule;
    }

    promises.push(updateUserProfile(newUserProfile));

    // promises.push(
    //   updateContactAttributeOperation(
    //     email,
    //     'consentToProductComms',
    //     consentToProductComms.toString(),
    //   ),
    // );

    try {
      await Promise.all(promises);
    } finally {
      dispatchAmplifyUser(dispatch);
    }
  };

  const handleSave = async () => {
    if (!givenName) {
      setErrorMessage('Please fill in your first name');
      return;
    }
    if (!familyName) {
      setErrorMessage('Please fill in your surname');
      return;
    }
    if (
      (cleartextPassword && !oldPassword) ||
      (!cleartextPassword && oldPassword)
    ) {
      setErrorMessage(
        'To change your password, you need to type your existing password as well.',
      );
      return;
    }
    try {
      setErrorMessage('');
      setIsLoading(true);
      await saveUpdates();
      dispatch(
        showGlobalSnackbar({
          notificationText: 'Changes saved',
        }),
      );
      setIsDirty(false);
    } catch (err) {
      console.log({ err });
      if (
        (err.name === 'InvalidParameterException' &&
          (err.message || '').match(
            /'password' failed to satisfy constraint/,
          )) ||
        err.name === 'InvalidPasswordException'
      ) {
        setErrorMessage(
          `Your password must be at least ${MINIMUM_PASSWORD_LENGTH} characters`,
        );
      } else if (err.name === 'NotAuthorizedException') {
        setErrorMessage(
          'You have typed your current password incorrectly, please try again',
        );
      } else if (err.name === 'LimitExceededException') {
        setErrorMessage('Please try again after a while');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const afterChange = () => {
    setIsDirty(true);
    setErrorMessage('');
  };

  const currentSpaceMembershipExpirationDate =
    currentSpaceMembership?.expiresAt;

  const isBeforeExpiration =
    currentSpaceMembershipExpirationDate &&
    !isTestUserExpiryDate(currentSpaceMembership) &&
    dayjs().isBefore(dayjs(currentSpaceMembershipExpirationDate));

  const handleClickDeleteAccount = () => {
    if (isBeforeExpiration) {
      setDeleteAccountDisallowedBeforeExpirationPopupVisible(true);
    } else {
      setDeleteAccountPopupVisible(true);
    }
  };

  const handleDeleteAccount = async () => {
    if (!userId || !email) {
      console.warn(
        'userId or email are not available, will not initiate account deletion.',
      );
      return;
    }
    try {
      await sendAccountDeletedNotificationOperation(userId, email);
      const result = await deleteUser();
      console.log(result);
      dispatch(signOutAction());
      navigate(
        linkWithQueryParams(LOGIN_ROUTE, {
          intoSpace: currentSpace?.id,
        }),
      );
    } catch (e) {
      console.log(e);
    }
  };

  const isCommunityUser = currentSpaceMembership?.spaceID === DEFAULT_SPACE_ID;

  const isPaymentRequired = !isCommunityUser;

  const currentSpaceMembershipTierID = currentSpaceMembership?.membershipTierID;

  const currentSpaceMembershipState = currentSpaceMembership?.state;

  const { data: membershipTiers } = useGetMembershipTiersBySpaceQuery(
    currentSpace?.id,
    {
      skip: !currentSpace?.id,
    },
  );

  const currentSpaceMembershipTierTitle = (membershipTiers || []).find(
    (mt) => mt.id === currentSpaceMembership?.id,
  )?.title;

  const hasMembershipTier = !!currentSpaceMembershipTierID;

  return (
    <div className="userProfile light-background">
      <div className="banner">Manage your personal information</div>
      <Grid container columnSpacing={1} className="userProfileForm">
        <Grid item xs={12}>
          <h5>Your name</h5>
        </Grid>
        <Grid item xs={6}>
          <TextField
            value={givenName}
            onChange={(ev) => {
              setGivenName(ev.target.value);
              afterChange();
            }}
            autoFocus
            margin="dense"
            label="First name *"
            autoComplete="given-name"
            type="text"
            fullWidth
            size="small"
            variant="outlined"
            disabled={isLoading}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            value={familyName}
            onChange={(ev) => {
              setFamilyName(ev.target.value);
              afterChange();
            }}
            margin="dense"
            label="Surname *"
            autoComplete="family-name"
            type="text"
            fullWidth
            size="small"
            variant="outlined"
            disabled={isLoading}
          />
        </Grid>
        <Grid item xs={12}>
          <h5>Your email address</h5>
        </Grid>
        <Grid item xs={12}>
          <TextField
            value={email}
            margin="dense"
            label="Email address *"
            type="email"
            fullWidth
            size="small"
            variant="outlined"
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <h5>Your password</h5>
        </Grid>
        <Grid item xs={6}>
          <TextField
            value={oldPassword}
            onChange={(ev) => {
              setOldPassword(ev.target.value);
              afterChange();
            }}
            margin="dense"
            label="Current password *"
            type="password"
            // Disables annoying Chrome autofill
            autoComplete="new-password"
            fullWidth
            size="small"
            variant="outlined"
            disabled={isLoading}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            value={cleartextPassword}
            onChange={(ev) => {
              setCleartextPassword(ev.target.value);
              afterChange();
            }}
            margin="dense"
            label="New password *"
            type="password"
            autoComplete="new-password"
            fullWidth
            size="small"
            variant="outlined"
            disabled={isLoading}
          />
        </Grid>
        {!skipNonEssentialFields && (
          <>
            <Grid item xs={12}>
              <h5>Your location</h5>
            </Grid>
            <Grid item xs={12}>
              <Select
                variant="standard"
                fullWidth
                displayEmpty
                value={geoRegion}
                onChange={(ev) => {
                  const newRegion = ev.target.value;
                  setGeoRegion(newRegion);
                  afterChange();
                }}
                inputProps={{ 'aria-label': 'Without label' }}>
                <MenuItem disabled value="">
                  <em>Select a location</em>
                </MenuItem>
                {GEO_REGIONS.map((region) => (
                  <MenuItem key={region.name} value={region.name}>
                    {region.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </>
        )}
        {!skipNonEssentialFields && (
          <>
            <Grid item xs={12}>
              <h5>Meal plan reminders</h5>
            </Grid>
            <Grid item xs={12}>
              <Select
                variant="standard"
                fullWidth
                displayEmpty
                value={mealPlanReminderSchedule}
                onChange={(ev) => {
                  const newScheduleName = ev.target.value;
                  setMealPlanReminderSchedule(newScheduleName);
                  afterChange();
                }}
                inputProps={{ 'aria-label': 'Without label' }}>
                <MenuItem disabled value="">
                  <em>Select a schedule</em>
                </MenuItem>
                {REMINDER_SCHEDULES.map((schedule) => (
                  <MenuItem key={schedule.name} value={schedule.name}>
                    {schedule.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </>
        )}
        {isPaymentRequired && (
          <>
            <Grid item xs={12}>
              <h5>Subscription tier</h5>
            </Grid>
            <Grid item xs={12} style={{ fontSize: '14px' }}>
              {!hasMembershipTier && 'No subscription yet'}
              {hasMembershipTier &&
                currentSpaceMembershipState === SpaceMembershipState.ACTIVE && (
                  <div style={{ fontSize: '14px' }}>
                    {currentSpaceMembershipTierTitle} (expires{' '}
                    {dayjs(currentSpaceMembershipExpirationDate).format(
                      'DD MMM YYYY',
                    )}
                    )
                    <br />
                    <UserProfileManageSubscriptionButton />
                  </div>
                )}
            </Grid>
            {hasMembershipTier &&
              currentSpaceMembershipState ===
                SpaceMembershipState.CANCELLED && (
                <div style={{ fontSize: '14px' }}>
                  {currentSpaceMembershipTierTitle} (expires{' '}
                  {dayjs(currentSpaceMembershipExpirationDate).format(
                    'DD MMM YYYY',
                  )}
                  )
                </div>
              )}
          </>
        )}
        <Grid item xs={12}>
          <h5>App info</h5>
        </Grid>
        <Grid item xs={12}>
          <AppTechnicalInfoLabel />
        </Grid>
        <Grid item xs={12} style={{ margin: '8px 0', padding: '6px' }}>
          {errorMessage && (
            <div style={{ paddingTop: '5px', color: 'red' }}>
              {errorMessage}
            </div>
          )}
          <Grid container>
            <Grid item xs={6}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate(-1)}>
                Back
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={!isDirty || isLoading}>
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ margin: '8px 0', padding: '6px' }}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a
            href="#"
            style={{ color: 'red', textDecoration: 'underline' }}
            onClick={(ev) => {
              ev.preventDefault();
              handleClickDeleteAccount();
            }}>
            Delete account
          </a>
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            fontSize: '12px',
            textAlign: 'center',
            marginBottom: '20px',
          }}>
          <Grid container>
            <Grid item xs={6}>
              <a href="https://www.smorg.io/terms" target="_new">
                Terms & Conditions
              </a>
            </Grid>
            <Grid item xs={6}>
              <a href="https://www.smorg.io/privacy" target="_new">
                Privacy Policy
              </a>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {deleteAccountPopupVisible && (
        <DeleteAccountPopup
          onDelete={handleDeleteAccount}
          onDismiss={() => setDeleteAccountPopupVisible(false)}
        />
      )}
      {deleteAccountDisallowedBeforeExpirationPopupVisible && (
        <DeleteAccountDisallowedBeforeExpirationPopup
          expirationDate={currentSpaceMembershipExpirationDate}
          onDismiss={() =>
            setDeleteAccountDisallowedBeforeExpirationPopupVisible(false)
          }
        />
      )}
    </div>
  );
};

export default UserProfileEndUserDesktopView;
